import React, { useEffect, useRef, useState } from "react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { AlertTriangle } from "lucide-react";
import { getPdfjs } from "../core/pdf/loadDocument";

interface PdfPageProps {
  doc: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
  rotation: number;
  /** Only pages near the viewport actually render their canvas/text (virtualization). */
  active: boolean;
  /** Reports the page's rendered pixel size so the viewer can size placeholders. */
  onSize?: (pageNumber: number, width: number, height: number) => void;
  /** Signature preview drawn over the page (fractions of the page box). */
  ghost?: SignatureGhost;
  /** When true, a click on the page reports its position via `onPlace`. */
  placing?: boolean;
  onPlace?: (pageNumber: number, xFrac: number, yFrac: number) => void;
}

/** Fraction → CSS percent, trimmed of float noise (0.55 * 100 ≠ 55 exactly). */
const percent = (fraction: number) => `${Number((fraction * 100).toFixed(3))}%`;

export interface SignatureGhost {
  image: string;
  xFrac: number;
  yFrac: number;
  widthFrac: number;
}

/** Drop a canvas's backing store immediately instead of waiting on GC — keeps
 *  memory flat while scrolling through large documents. */
const releaseCanvas = (canvas: HTMLCanvasElement | null) => {
  if (!canvas) return;
  canvas.width = 0;
  canvas.height = 0;
};

/**
 * Renders a single PDF page to a canvas plus a selectable text layer. When
 * `active` is false it collapses to a correctly-sized placeholder so the
 * scroll height stays stable while keeping memory flat on large documents.
 */
const PdfPage: React.FC<PdfPageProps> = ({
  doc,
  pageNumber,
  scale,
  rotation,
  active,
  onSize,
  ghost,
  placing,
  onPlace,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const textLayerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!active) return;
    let cancelled = false;
    let renderTask: { cancel: () => void } | null = null;
    setFailed(false);
    // Captured now (while the canvas is still mounted) rather than read from
    // the ref inside the cleanup below, since deactivating the page unmounts
    // the <canvas> and nulls the ref before this cleanup runs.
    const canvasEl = canvasRef.current;

    (async () => {
      try {
        const pdfjs = await getPdfjs();
        const page = await doc.getPage(pageNumber);
        if (cancelled) return;

        const viewport = page.getViewport({ scale, rotation });
        const width = Math.floor(viewport.width);
        const height = Math.floor(viewport.height);
        setSize({ width, height });
        onSize?.(pageNumber, width, height);

        const canvas = canvasRef.current;
        if (!canvas) return;
        const context = canvas.getContext("2d");
        if (!context) return;

        // Render above the display resolution (supersampling) so thin vector
        // lines — CAD drawings, tables, hairlines — stay crisp instead of being
        // anti-aliased into blur. We go to device-pixel-ratio × a quality factor,
        // capped so the backing canvas can't exceed the browser's limits or eat
        // too much memory at high zoom.
        const dpr = window.devicePixelRatio || 1;
        const QUALITY = 2;
        const MAX_SIDE = 5000;
        const longest = Math.max(width, height);
        let outputScale = dpr * QUALITY;
        if (longest * outputScale > MAX_SIDE) outputScale = MAX_SIDE / longest;
        outputScale = Math.max(outputScale, dpr); // never below crisp 1:1 for the display

        canvas.width = Math.round(width * outputScale);
        canvas.height = Math.round(height * outputScale);
        canvas.style.width = `${width}px`;
        canvas.style.height = `${height}px`;

        const task = page.render({
          canvasContext: context,
          viewport,
          transform: [outputScale, 0, 0, outputScale, 0, 0],
        });
        renderTask = task;
        try {
          await task.promise;
        } catch {
          return; // render cancelled on scale/rotation change — safe to ignore
        }
        if (cancelled) return;

        // Selectable, copyable text layer aligned over the canvas.
        const textLayer = textLayerRef.current;
        if (textLayer) {
          textLayer.innerHTML = "";
          // pdf.js sizes/positions text-layer spans via calc(var(--scale-factor) * ...);
          // without this set, those calc() expressions are invalid and selection breaks.
          textLayer.style.setProperty("--scale-factor", String(scale));
          textLayer.style.width = `${width}px`;
          textLayer.style.height = `${height}px`;
          const textContent = await page.getTextContent();
          if (cancelled) return;
          pdfjs.renderTextLayer({
            textContentSource: textContent,
            container: textLayer,
            viewport,
            textDivs: [],
          } as Parameters<typeof pdfjs.renderTextLayer>[0]);
        }
      } catch {
        // A single malformed page shouldn't take down the rest of the
        // document — show an inline error and leave neighboring pages alone.
        if (!cancelled) setFailed(true);
      }
    })();

    return () => {
      cancelled = true;
      renderTask?.cancel();
      releaseCanvas(canvasEl);
    };
  }, [doc, pageNumber, scale, rotation, active, onSize]);

  const placeholderStyle: React.CSSProperties = size
    ? { width: size.width, height: size.height }
    : { width: 612 * scale, height: 792 * scale }; // US-Letter fallback before measure

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!placing || !onPlace) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const xFrac = (e.clientX - rect.left) / rect.width;
    const yFrac = (e.clientY - rect.top) / rect.height;
    onPlace(pageNumber, Math.min(Math.max(xFrac, 0), 0.9), Math.min(Math.max(yFrac, 0), 0.9));
  };

  return (
    <div
      className={`pdf-page${placing ? " pdf-page-placing" : ""}`}
      data-page={pageNumber}
      style={placeholderStyle}
      onClick={handleClick}
    >
      {ghost && active && (
        <img
          className="sig-ghost"
          src={ghost.image}
          alt="Signature preview"
          draggable={false}
          style={{
            left: percent(ghost.xFrac),
            top: percent(ghost.yFrac),
            width: percent(ghost.widthFrac),
          }}
        />
      )}
      {active ? (
        failed ? (
          <div className="pdf-page-error">
            <AlertTriangle size={20} />
            <span>Page {pageNumber} couldn't be rendered</span>
          </div>
        ) : (
          <>
            <canvas ref={canvasRef} className="pdf-page-canvas" />
            <div ref={textLayerRef} className="pdf-text-layer" />
          </>
        )
      ) : (
        <div className="pdf-page-placeholder">
          <span>{pageNumber}</span>
        </div>
      )}
    </div>
  );
};

export default React.memo(PdfPage);
